import React, { useState, useEffect } from 'react';
import type { DoctorEducation, Specialization, Subspecialization } from '../../../types/doctorOnboarding';
import { uploadFile } from '../../../services/doctor-onboarding/doctorOnboardingService';
import { formatStepError } from './stepError';
import { getFileNameFromUrl } from './fileName';

type Props = {
  doctorId: number | null;
  entries: DoctorEducation[];
  degreeTypes: Array<{ id: number; name: string; code: string; is_active: boolean }>;
  specializations: Specialization[];
  subspecializations: Subspecialization[];
  onLoadSubspecializations: (specializationId: number) => Promise<void>;
  onAdd: (body: Omit<DoctorEducation, 'id'>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
};

export default function EducationStep({
  doctorId,
  entries,
  degreeTypes,
  specializations,
  subspecializations,
  onLoadSubspecializations,
  onAdd,
  onDelete,
  onNext,
  onBack,
}: Props) {
  const [degreeType, setDegreeType] = useState('');
  const [degreeName, setDegreeName] = useState('');
  const [institution, setInstitution] = useState('');
  const [university, setUniversity] = useState('');
  const [year, setYear] = useState<number | ''>('');
  const [specializationId, setSpecializationId] = useState<number | ''>('');
  const [subspecializationId, setSubspecializationId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (specializationId !== '') {
      onLoadSubspecializations(Number(specializationId));
      setSubspecializationId('');
    }
  }, [specializationId, onLoadSubspecializations]);

  const canContinue = entries.length > 0;

  const currentYear = new Date().getFullYear();
  const yearNumber = year === '' ? NaN : Number(year);
  const yearValid =
    Number.isFinite(yearNumber) &&
    yearNumber >= 1950 &&
    yearNumber <= currentYear;

  const isValid =
    !!doctorId &&
    degreeType &&
    degreeName.trim() &&
    institution.trim() &&
    university.trim() &&
    yearValid &&
    file;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !doctorId || !file) return;
    setLoading(true);
    setError(null);
    try {
      const upload = await uploadFile(file, 'degrees');
      await onAdd({
        doctor_id: doctorId,
        degree_type: degreeType,
        degree_name: degreeName.trim(),
        institution_name: institution.trim(),
        university_name: university.trim(),
        year_of_completion: yearNumber,
        certificate_document_url: upload.url,
        display_order: entries.length,
        specialization_id: specializationId === '' ? undefined : Number(specializationId),
        subspecialization_id: subspecializationId === '' ? undefined : Number(subspecializationId),
      } as DoctorEducation);
      setDegreeType('');
      setDegreeName('');
      setInstitution('');
      setUniversity('');
      setYear('');
      setSpecializationId('');
      setSubspecializationId('');
      setFile(null);
      setFileInputKey((prev) => prev + 1);
    } catch (e: any) {
      setError(formatStepError(e, 'Failed to add education'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="onboarding-section">
        <h3>Education</h3>
        <form onSubmit={handleAdd}>
          <div className="form-group">
            <label>Degree Type <span className="required">*</span></label>
            <select value={degreeType} onChange={(e) => setDegreeType(e.target.value)}>
              <option value="">Select</option>
            {degreeTypes.filter((d) => d.is_active).map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Degree Name <span className="required">*</span></label>
            <input value={degreeName} onChange={(e) => setDegreeName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Specialization (optional)</label>
            <select
              value={specializationId === '' ? '' : specializationId}
              onChange={(e) => setSpecializationId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Select</option>
              {specializations.filter((s) => s.is_active).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Subspecialization (optional)</label>
            <select
              value={subspecializationId === '' ? '' : subspecializationId}
              onChange={(e) => setSubspecializationId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={specializationId === ''}
            >
              <option value="">Select</option>
              {subspecializations.filter((s) => s.is_active).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Institution Name <span className="required">*</span></label>
            <input value={institution} onChange={(e) => setInstitution(e.target.value)} />
          </div>
          <div className="form-group">
            <label>University Name <span className="required">*</span></label>
            <input value={university} onChange={(e) => setUniversity(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Year of Completion <span className="required">*</span></label>
            <input
              type="number"
              min={1950}
              max={currentYear}
              value={year}
              onChange={(e) =>
                setYear(e.target.value === '' ? '' : Number(e.target.value))
              }
            />
            {year && !yearValid && (
              <span className="field-hint">Enter a valid 4-digit year</span>
            )}
          </div>
          <div className="form-group">
            <label>Certificate Upload <span className="required">*</span></label>
            <input
              key={fileInputKey}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          {error && <div className="onboarding-error">{error}</div>}
          <button type="submit" className="btn-primary" disabled={!isValid || loading}>
            {loading ? 'Uploading...' : 'Add Education'}
          </button>
        </form>
      </div>

      <div className="onboarding-section">
        <h3>Existing Entries</h3>
        {entries.length === 0 && <p className="onboarding-step-label">No education entries yet.</p>}
        {entries.map((e) => (
          <div key={e.id} className="onboarding-list-item">
            <div className="onboarding-list-meta">
              <div>
                <strong>{e.degree_type}</strong> — {e.degree_name} ({e.year_of_completion})
              </div>
              {(e.specialization_name || e.subspecialization_name) && (
                <div className="onboarding-step-label">
                  {[e.specialization_name, e.subspecialization_name].filter(Boolean).join(' / ')}
                </div>
              )}
              <div className="onboarding-step-label">Institution: {e.institution_name}</div>
              <div className="onboarding-step-label">University: {e.university_name}</div>
              {getFileNameFromUrl(e.certificate_document_url) && (
                <div className="onboarding-step-label onboarding-file-name">
                  File: {getFileNameFromUrl(e.certificate_document_url)}
                </div>
              )}
            </div>
            <button type="button" className="btn-secondary" onClick={() => onDelete(e.id)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        {!canContinue && (
          <span className="field-hint">Add at least one education entry to continue.</span>
        )}
        <button type="button" className="btn-primary" onClick={onNext} disabled={!canContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}
